import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Camera, X, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";

const LOGO_URL = "https://res.cloudinary.com/dmivaa0e0/image/upload/v1772834626/branding/logo_badge.png";

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [picture, setPicture] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      // Get signature from backend (public endpoint for registration)
      const sigResponse = await axios.get(`${API}/uploads/public-signature?folder=profiles`);
      const sig = sigResponse.data;

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sig.api_key);
      formData.append("timestamp", sig.timestamp);
      formData.append("signature", sig.signature);
      formData.append("folder", sig.folder);

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: "POST", body: formData }
      );

      if (!uploadResponse.ok) {
        throw new Error("Upload failed");
      }

      const result = await uploadResponse.json();
      setPicture(result.secure_url);
      toast.success("Photo uploaded!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    // Validate username format
    if (username && !/^[a-zA-Z0-9_]{3,20}$/.test(username)) {
      toast.error("Username must be 3-20 characters (letters, numbers, underscores only)");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, { 
        name, 
        email, 
        password,
        username: username || null,
        picture: picture || null
      });
      login(response.data);
      toast.success("Welcome to Rides & Dives!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mobile-container asphalt-texture min-h-screen flex flex-col p-4">
      {/* Header with Back Button */}
      <div className="flex items-center mb-4">
        <Button
          data-testid="back-btn"
          variant="ghost"
          size="icon"
          className="rounded-full"
          onClick={() => navigate("/")}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
      </div>

      {/* Large Centered Logo */}
      <div className="flex justify-center mb-6">
        <img 
          src={LOGO_URL} 
          alt="Rides and Dives" 
          className="w-1/2 max-w-[180px] h-auto"
        />
      </div>

      {/* Form */}
      <div className="flex-1 flex flex-col justify-center">
        <h1 className="font-oswald text-3xl font-bold uppercase mb-2 text-center">Join The Ride</h1>
        <p className="text-gray-400 text-sm mb-6 text-center">Create your account and start planning</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Profile Photo Upload */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                id="photo-upload"
                disabled={uploading}
              />
              <label
                htmlFor="photo-upload"
                className="cursor-pointer block"
              >
                {picture ? (
                  <div className="relative">
                    <img
                      src={picture}
                      alt="Profile"
                      className="w-24 h-24 rounded-full object-cover border-2 border-bronze"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setPicture(null);
                      }}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-full bg-[#121212] border-2 border-dashed border-white/20 hover:border-bronze/50 flex flex-col items-center justify-center transition-colors">
                    {uploading ? (
                      <Loader2 className="w-8 h-8 text-bronze animate-spin" />
                    ) : (
                      <>
                        <Camera className="w-8 h-8 text-gray-500" />
                        <span className="text-[10px] text-gray-500 mt-1">Add Photo</span>
                      </>
                    )}
                  </div>
                )}
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm text-gray-400 uppercase tracking-wide">Name</Label>
            <Input
              id="name"
              data-testid="name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Road Warrior"
              className="h-12 bg-[#121212] border-white/10 focus:border-bronze placeholder:text-gray-600"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="username" className="text-sm text-gray-400 uppercase tracking-wide">
              Username <span className="text-gray-600">(optional)</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">@</span>
              <Input
                id="username"
                data-testid="username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                placeholder="road_warrior"
                className="h-12 bg-[#121212] border-white/10 focus:border-bronze placeholder:text-gray-600 pl-8"
                maxLength={20}
              />
            </div>
            <p className="text-[10px] text-gray-600">3-20 characters, letters, numbers, underscores only</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-gray-400 uppercase tracking-wide">Email</Label>
            <Input
              id="email"
              data-testid="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="rider@example.com"
              className="h-12 bg-[#121212] border-white/10 focus:border-bronze placeholder:text-gray-600"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-gray-400 uppercase tracking-wide">Password</Label>
            <div className="relative">
              <Input
                id="password"
                data-testid="password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 bg-[#121212] border-white/10 focus:border-bronze placeholder:text-gray-600 pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                data-testid="toggle-password-btn"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            data-testid="register-submit-btn"
            type="submit"
            disabled={loading || uploading}
            className="w-full h-14 bg-bronze hover:bg-bronze-600 font-oswald text-lg uppercase tracking-wider neon-bronze btn-primary"
          >
            {loading ? "Creating Account..." : "Create Account"}
          </Button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-[#050505] px-2 text-gray-500">Or continue with</span>
          </div>
        </div>

        <Button
          data-testid="google-register-btn"
          variant="outline"
          className="w-full h-12 border-white/20 hover:bg-white/5 font-medium"
          onClick={handleGoogleLogin}
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </Button>

        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-bronze hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
